import { useEffect, useState } from "react";
import type { MainTab, ReportConfigurationState, ReportSubTab } from "../types";

const STORAGE_KEY = "reportConfiguration.selection";
// "scheduled" and "log" are hidden until Phase 2, so a saved selection pointing at them is not restored.
const MAIN_TABS: MainTab[] = ["plan", "reports", "globalTemplates"];
const REPORT_TABS: ReportSubTab[] = ["setup", "categorization", "metrics", "excel"];
let reloadSelectionConsumed = false;

function isReportConfigurationReload() {
    if (typeof window === "undefined" || reloadSelectionConsumed) return false;
    const navigation = window.performance?.getEntriesByType?.("navigation")[0] as PerformanceNavigationTiming | undefined;
    if (navigation?.type !== "reload") return false;
    try {
        // The navigation entry retains the document's original URL after SPA navigation.
        return new URL(navigation.name).pathname === window.location.pathname;
    } catch {
        return false;
    }
}

export type ReportConfigurationSelection = {
    mainTab?: MainTab;
    reportSubTab?: ReportSubTab;
    selectedPlanId?: string;
    selectedFolderId?: string;
    selectedReportId?: string;
};

export function readReportConfigurationSelection(): ReportConfigurationSelection {
    if (typeof window === "undefined") return {};
    try {
        const parsed = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || "{}");
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
        if (parsed.pathname !== window.location.pathname) return {};
        return {
            mainTab: MAIN_TABS.includes(parsed.mainTab) ? parsed.mainTab : undefined,
            reportSubTab: REPORT_TABS.includes(parsed.reportSubTab) ? parsed.reportSubTab : undefined,
            selectedPlanId: typeof parsed.selectedPlanId === "string" ? parsed.selectedPlanId : undefined,
            selectedFolderId: typeof parsed.selectedFolderId === "string" ? parsed.selectedFolderId : undefined,
            selectedReportId: typeof parsed.selectedReportId === "string" ? parsed.selectedReportId : undefined,
        };
    } catch {
        return {};
    }
}

export function writeReportConfigurationSelection(selection: ReportConfigurationSelection) {
    if (typeof window === "undefined" || !selection.selectedPlanId) return;
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...selection, pathname: window.location.pathname }));
    } catch {
        // Storage may be unavailable; the editor still works without persistence.
    }
}

export function useReportConfigurationSelectionPersistence(selection: ReportConfigurationSelection, ready: boolean) {
    const { mainTab, reportSubTab, selectedPlanId, selectedFolderId, selectedReportId } = selection;
    useEffect(() => {
        if (!ready) return;
        writeReportConfigurationSelection({ mainTab, reportSubTab, selectedPlanId, selectedFolderId, selectedReportId });
    }, [ready, mainTab, reportSubTab, selectedPlanId, selectedFolderId, selectedReportId]);
}

export function useReportConfigurationSelection(data: ReportConfigurationState) {
    const [initialSelection] = useState(() => isReportConfigurationReload() ? readReportConfigurationSelection() : {});
    useEffect(() => {
        // Consume on commit, so StrictMode's repeated initializers see the same selection.
        reloadSelectionConsumed = true;
        try {
            window.localStorage.removeItem(STORAGE_KEY);
        } catch {
            // Removing the old persistent preference must not block the editor.
        }
        if (!initialSelection.selectedPlanId) {
            try {
                window.sessionStorage.removeItem(STORAGE_KEY);
            } catch {
                // A fresh route entry still uses defaults when storage is unavailable.
            }
        }
    }, [initialSelection]);
    const plan = data.plans.find((item) => item.id === initialSelection.selectedPlanId) || data.plans[0];
    const folder = plan?.reportFolders.find((item) => item.id === initialSelection.selectedFolderId) || plan?.reportFolders[0];
    const report = folder?.reports.find((item) => item.id === initialSelection.selectedReportId) || folder?.reports[0];
    const [selectedPlanId, setSelectedPlanId] = useState(plan?.id || "");
    const [selectedFolderId, setSelectedFolderId] = useState(folder?.id || "");
    const [selectedReportId, setSelectedReportId] = useState(report?.id || "");
    const [mainTab, setMainTab] = useState<MainTab>(initialSelection.mainTab || "reports");
    const [reportSubTab, setReportSubTab] = useState<ReportSubTab>(initialSelection.reportSubTab || "setup");
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(folder ? [folder.id] : []));

    return {
        initialSelection,
        selectedPlanId, setSelectedPlanId,
        selectedFolderId, setSelectedFolderId,
        selectedReportId, setSelectedReportId,
        mainTab, setMainTab,
        reportSubTab, setReportSubTab,
        expandedFolders, setExpandedFolders,
    };
}
